/**
 * Pure shaping helpers for the Pilot Trends admin page (feat-146).
 *
 * The backend (`GET /api/pilot-trends`, ADR-0001 §4) returns FLAT per-(store × day)
 * rows and OMITS rows for the Jul 2–5 gap and for dates > today. On a Recharts
 * category x-axis, absent dates make a line connect across them (no break). So the
 * FE must (1) build the full date domain [window.start .. window.end], (2) null-pad
 * every date with no backend row, and (3) render with `connectNulls={false}`. That
 * yields a real break across the gap and an empty reserved tail that fills in as
 * pilot days accrue. This module does (1) + (2) + the flat→wide reshape.
 *
 * IMPORTANT — no metric math here. The Upsell-Score / attempt / close-rate FORMULAS
 * live only in the backend (memory: feedback_metrics_single_source). The only
 * derivations below are AGGREGATIONS of backend-provided per-day values — the 7-day
 * rolling mean and the test/control group averages — which ADR-0001 §5 explicitly
 * permits (they match the feat-096 precedent). Kept pure so they are unit-tested
 * without React.
 */
import type {
  PilotStore,
  PilotTrendsResponse,
  PilotPhaseName,
} from '../../../services/pilotTrendsService';

// ── Cohort colors — the single TS source, MUST mirror `tailwind.config.js`
//    `theme.extend.colors.pilot` and `.team/design/tokens/TOKENS.md §1k`.
//    Recharts stroke/fill props take runtime values and Tailwind's `theme()`
//    helper does NOT resolve inside JSX, so the hexes live here once (referenced
//    by name, never inlined per-chart) rather than scattered as raw literals.
export const PILOT_COLORS = {
  // Okabe-Ito colorblind-safe set (feat-151, mirrors tailwind.config.js + TOKENS.md §1k).
  test: { a: '#D55E00', b: '#C64C97' }, // a = vermillion (580 California); b = magenta (1 California)
  control: { a: '#0072B2', b: '#009E73' }, // a = blue (221 Montgomery); b = bluish-green (City Center)
} as const;

// Reference lines are goal/baseline semantics, deliberately NOT cohort colors.
export const REF_COLORS = {
  target: '#E9A23B', // = gold (the bogey / goal)
  targetLabel: '#B4801F',
  benchmark: '#717182', // = inkMuted (neutral baseline, reads as a baseline not a series)
} as const;

// Phase-band + today-marker fills.
//
// ⚠ feat-229 resolves `pre` BY HEX, not by the token NAME `cream-dark`: that name
// means `#F5EFE6` in the React binding (`tailwind.config.js` `colors.cream.dark`)
// and `#ECECF0` in `.team/design/tokens/TOKENS.md §1i`. The mismatch is logged
// under TOKENS.md → "Known inconsistencies" and is deliberately NOT fixed here.
// `#F5EFE6` — the app's own warm canvas step — is the value the design approved.
export const BAND = {
  pre: '#F5EFE6',
  benchmark: '#E9A23B',
  gap: '#717182',
  pilot: '#D55847',
  today: '#2D2520',
} as const;

/**
 * Band fill-opacity is a function of band WIDTH, not a constant — a rule, not a
 * divergence (`TOKENS.md §1m`; DESIGN-NOTES §3.2). A band's job is to be findable
 * and findability scales with area, so a band under ~10% of the x-axis takes
 * roughly 2× its default opacity.
 *
 * Benchmark is 18% of the existing card's axis and 6% of the long view's, so it
 * goes 0.07 → 0.15; pilot 70% → 19%, so 0.05 → 0.09. Same hexes both times.
 * **Do not "correct" these back to the existing card's values.**
 *
 * The pre band is a flat OPAQUE wash (1.0) rather than a fifth tint: it covers 71%
 * of the panel, and any hue strong enough to read across that area would compete
 * with the 19%-wide pilot band and invert the card's message.
 */
export const LONG_VIEW_BAND_OPACITY = {
  pre: 1.0,
  benchmark: 0.15,
  gap: 0.1,
  pilot: 0.09,
} as const;

export type SeriesMode = 'group' | 'store';
export type CohortGroup = 'test' | 'control';

export interface StoreWithColor extends PilotStore {
  /** cohort color resolved from PILOT_COLORS by (group, index-within-group). */
  color: string;
}

/** Wide row consumed by Recharts: one object per calendar date, keyed by store. */
export interface WideRow {
  date: string;
  phase: PilotPhaseName | 'gap';
  [key: string]: number | null | string;
}

/** Inclusive list of ISO dates from `start` to `end` (noon anchor dodges DST edges). */
export function eachDate(start: string, end: string): string[] {
  const out: string[] = [];
  const cur = new Date(`${start}T12:00:00`);
  const last = new Date(`${end}T12:00:00`);
  while (cur <= last) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Which phase a date falls in, from the backend-provided phase windows. */
export function phaseOf(
  date: string,
  phases: PilotTrendsResponse['phases'],
): PilotPhaseName | 'gap' {
  for (const p of phases) {
    if (date >= p.start && date <= p.end) return p.phase;
  }
  return 'gap';
}

/**
 * Attach a cohort color to each store, assigned by group + index-within-group
 * (first test → test.a, second test → test.b, etc.). Generalizes past the four
 * hard-coded L&L stores while matching the approved palette.
 */
export function assignStoreColors(stores: PilotStore[]): StoreWithColor[] {
  const seen: Record<CohortGroup, number> = { test: 0, control: 0 };
  return stores.map((s) => {
    const idx = seen[s.group]++;
    const ramp = PILOT_COLORS[s.group];
    const color = idx === 0 ? ramp.a : ramp.b; // 3rd+ store in a group reuses hue b
    return { ...s, color };
  });
}

// Metric key suffixes on a wide row: `${location_id}${suffix}`.
// `_ring` (AOV $) + `_ipt` (items/txn) are feat-152's coverage-independent POS
// aggregates (present for controls too) — null-padded over the full domain like
// every other series so `connectNulls={false}` yields a real gap break.
// `_rev` (daily revenue $) + `_txn` (daily transaction count) are feat-179's
// store-level read — Ring's numerator and denominator, same coverage-independent
// presence rule as `_ring`/`_ipt`, so controls carry real lines here too.
// ⚠ `_txn` is NOT `_total`: `_total` is `orders_same_date` (chart 2's halo
// series) and `_txn` is the Ring denominator. They come from different backend
// fields and must stay distinct keys even if they happen to agree numerically.
const SUFFIXES = ['', '_pp', '_at', '_incov', '_total', '_close', '_att', '_cov', '_ring', '_ipt', '_rev', '_txn'] as const;

function nullRow(id: string): Record<string, number | null> {
  const o: Record<string, number | null> = {};
  SUFFIXES.forEach((suf) => {
    o[id + suf] = null;
  });
  return o;
}

/**
 * Flat contract rows → wide rows over the FULL date domain, null-padded.
 *
 * `closeCriterion` selects which close-rate the `_close` keys carry: 'all' →
 * `order_close_rate`; otherwise the matching entry in `close_rate_by_criterion`
 * (missing → null). Also emits test/control group averages (`testAvg*` / `ctrlAvg*`)
 * as null-safe means of the per-store values.
 */
export function reshapeToWide(
  resp: PilotTrendsResponse,
  closeCriterion: string = 'all',
): WideRow[] {
  const stores = resp.stores;
  const test = stores.filter((s) => s.group === 'test');
  const ctrl = stores.filter((s) => s.group === 'control');

  // date -> location_id -> flat row
  const byDate = new Map<string, Map<string, PilotTrendsResponse['series'][number]>>();
  for (const row of resp.series) {
    if (!byDate.has(row.date)) byDate.set(row.date, new Map());
    byDate.get(row.date)!.set(row.location_id, row);
  }

  const dates = eachDate(resp.window.start, resp.window.end);

  return dates.map((date) => {
    const perStore = byDate.get(date);
    const wide: WideRow = { date, phase: phaseOf(date, resp.phases) };

    stores.forEach((s) => {
      const r = perStore?.get(s.location_id);
      if (!r) {
        Object.assign(wide, nullRow(s.location_id));
        return;
      }
      const closeVal =
        closeCriterion === 'all'
          ? r.order_close_rate
          : r.close_rate_by_criterion?.[closeCriterion] ?? null;
      wide[s.location_id] = r.upsell_score;
      wide[s.location_id + '_pp'] = r.pp_score;
      wide[s.location_id + '_at'] = r.att_score;
      wide[s.location_id + '_incov'] = r.orders_in_coverage;
      wide[s.location_id + '_total'] = r.orders_same_date;
      wide[s.location_id + '_close'] = closeVal;
      wide[s.location_id + '_att'] = r.recorded_attempts;
      wide[s.location_id + '_cov'] = r.coverage_pct;
      wide[s.location_id + '_ring'] = r.ring;
      wide[s.location_id + '_ipt'] = r.ipt;
      wide[s.location_id + '_rev'] = r.revenue;
      wide[s.location_id + '_txn'] = r.transactions;
    });

    const avg = (list: PilotStore[], suffix: string): number | null => {
      const vals = list
        .map((s) => wide[s.location_id + suffix])
        .filter((v): v is number => typeof v === 'number');
      if (!vals.length) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 1000) / 1000;
    };
    SUFFIXES.forEach((suf) => {
      wide['testAvg' + suf] = avg(test, suf);
      wide['ctrlAvg' + suf] = avg(ctrl, suf);
    });

    return wide;
  });
}

/**
 * Trailing 7-day rolling mean of a wide-row key, null-safe (mean of the available
 * points in the window; null when the window has no data). A pure aggregation of
 * backend values — not metric math.
 */
export function rollingMean(
  rows: WideRow[],
  key: string,
  windowSize = 7,
): (number | null)[] {
  return rows.map((_, i) => {
    const win: number[] = [];
    for (let j = Math.max(0, i - (windowSize - 1)); j <= i; j++) {
      const v = rows[j][key];
      if (typeof v === 'number') win.push(v);
    }
    if (!win.length) return null;
    return Math.round((win.reduce((a, b) => a + b, 0) / win.length) * 1000) / 1000;
  });
}

/** Clone rows, adding a `${key}__roll` column for each requested key. */
export function withRolling(rows: WideRow[], keys: string[]): WideRow[] {
  const cols: Record<string, (number | null)[]> = {};
  keys.forEach((k) => {
    cols[k] = rollingMean(rows, k);
  });
  return rows.map((row, i) => {
    const r: WideRow = { ...row };
    keys.forEach((k) => {
      r[k + '__roll'] = cols[k][i];
    });
    return r;
  });
}

/**
 * Mean of a wide-row key over the BENCHMARK-phase rows only (null if the benchmark
 * window has no numeric data for that key). Pure aggregation of backend values —
 * the anchor for the "% of benchmark" indexed lens (feat-152 Lens B).
 */
export function benchmarkMean(rows: WideRow[], key: string): number | null {
  const vals = rows
    .filter((r) => r.phase === 'benchmark')
    .map((r) => r[key])
    .filter((v): v is number => typeof v === 'number');
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * Clone rows, adding a `${key}_idx` column = value ÷ (that key's benchmark-window
 * mean) × 100, rounded to 0.1, null-safe. Indexing Ring and IPT each to its OWN
 * benchmark average = 100% puts two wildly-different-scaled metrics on ONE honest
 * axis, so co-movement reads without dual-axis scale games (feat-152 Lens B). Like
 * `rollingMean` / group averages this is a pure aggregation of backend values, NOT
 * metric re-derivation (memory: feedback_metrics_single_source). Keys whose
 * benchmark mean is null/0 index to `null` (no baseline to normalize against).
 */
export function withIndexed(rows: WideRow[], keys: string[]): WideRow[] {
  const bench: Record<string, number | null> = {};
  keys.forEach((k) => {
    bench[k] = benchmarkMean(rows, k);
  });
  return rows.map((row) => {
    const r: WideRow = { ...row };
    keys.forEach((k) => {
      const b = bench[k];
      const v = row[k];
      r[k + '_idx'] =
        typeof v === 'number' && b != null && b !== 0
          ? Math.round((v / b) * 1000) / 10
          : null;
    });
    return r;
  });
}

// ── feat-162: overall trend lines ─────────────────────────────────────────────

/** Trend styles offered by the global TREND control (Decision 2 / Question A(i)). */
export type TrendStyle = 'linear' | 'rolling14';

/**
 * The alternate trend style's window. Deliberately LONGER than the 7-day
 * SMOOTHING control (which smooths the RAW line) so the two never read as the
 * same knob — product-lead's Question A ruling, Option (i).
 */
export const TREND_ROLLING_WINDOW = 14;

/** Suffix of the fitted column `withFit` writes for each key. */
export const FIT_SUFFIX = '__fit';

/** Least-squares coefficients in index space; `null` when no line may be drawn. */
export interface FitCoeffs {
  slope: number;
  intercept: number;
}

/**
 * Least-squares coefficients over the NON-NULL `(rowIndex, value)` pairs of a key.
 *
 * x is the row's index in the full date domain (`eachDate`), NOT a position among
 * the surviving points — so the Jul 2–5 gap days keep their real x-positions and
 * the slope is not distorted by their absence (Decision 5). Null days are excluded
 * from the fit entirely; they are never read as zeros.
 *
 * Returns `null` for the degenerate cases that must render NO line at all:
 * fewer than 2 non-null points (never draw a flat line through one dot), an
 * all-null series, or zero variance in x (a single distinct x).
 */
export function linearFitCoeffs(rows: WideRow[], key: string): FitCoeffs | null {
  const xs: number[] = [];
  const ys: number[] = [];
  rows.forEach((r, i) => {
    const v = r[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      xs.push(i);
      ys.push(v);
    }
  });
  if (xs.length < 2) return null;

  const n = xs.length;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    sxx += dx * dx;
    sxy += dx * (ys[i] - meanY);
  }
  if (sxx === 0) return null; // zero x-variance → no line

  const slope = sxy / sxx;
  return { slope, intercept: meanY - slope * meanX };
}

/**
 * First and last row index carrying a real value for `key`, or `null` if none.
 * This is the span the trend is allowed to occupy — see `clampToObserved`.
 */
export function observedRange(rows: WideRow[], key: string): [number, number] | null {
  let first = -1;
  let last = -1;
  rows.forEach((r, i) => {
    if (typeof r[key] === 'number' && Number.isFinite(r[key] as number)) {
      if (first === -1) first = i;
      last = i;
    }
  });
  return first === -1 ? null : [first, last];
}

/**
 * Null out anything outside the key's observed range — the trend may span the
 * INTERIOR (the Jul 2–5 gap) but must never run past the real data at either end.
 *
 * Trailing: `eachDate` spans the FULL window, whose tail is deliberately reserved
 * and null-padded for pilot days that HAVEN'T HAPPENED YET (feat-146). A fit
 * emitted there marches across empty space to the window end and reads as a
 * forecast — on the one panel whose tail exists precisely to be filled in later.
 * The trend is an eyeball aid, not an inference (Decision 3: window start →
 * *today*; explicitly out of scope: "any statistical-significance claim").
 *
 * Leading: the mirror image. A store onboarded mid-window would otherwise have
 * its trend drawn backwards across dates it never recorded — backcasting invented
 * history, which is the same error pointed the other way.
 *
 * `connectNulls={true}` still bridges the interior gap; these outer nulls simply
 * end the line. (Found on the live page by feat-162-qa: every unit test passed
 * because the fixtures all ended at their last data point.)
 */
function clampToObserved(
  series: (number | null)[],
  rows: WideRow[],
  key: string,
): (number | null)[] | null {
  const range = observedRange(rows, key);
  if (!range) return null;
  const [first, last] = range;
  return series.map((v, i) => (i >= first && i <= last ? v : null));
}

/**
 * Fitted value for every date WITHIN the key's observed range (Decision 5): the
 * line draws continuously across the interior Jul 2–5 gap — which is what
 * `connectNulls={true}` needs — while the raw line still breaks there. Outside
 * that range the fit is null, so the trend never extrapolates into the reserved
 * future tail or backwards before the series began (see `clampToObserved`).
 *
 * `null` (no line at all) in the degenerate cases `linearFitCoeffs` rejects.
 */
export function linearFit(rows: WideRow[], key: string): (number | null)[] | null {
  const c = linearFitCoeffs(rows, key);
  if (!c) return null;
  const line = rows.map((_, i) => Math.round((c.slope * i + c.intercept) * 1000) / 1000);
  return clampToObserved(line, rows, key);
}

/**
 * A key's trend series in the selected style, or `null` for no line.
 *
 * The 'rolling14' style reuses the existing null-safe `rollingMean` with a longer
 * window rather than introducing a second smoother (memory:
 * feedback_metrics_single_source). Both styles honour the same <2-non-null-points
 * guard, so a sparse series never sprouts a trend under one style but not the other.
 */
export function fitLine(
  rows: WideRow[],
  key: string,
  style: TrendStyle,
): (number | null)[] | null {
  if (style === 'linear') return linearFit(rows, key);
  const points = rows.filter((r) => typeof r[key] === 'number').length;
  if (points < 2) return null;
  // `rollingMean` does NOT stop at the last real point: its trailing window keeps
  // finding that point for another `TREND_ROLLING_WINDOW - 1` days and emits a
  // flat mean across the reserved tail. Smaller than the least-squares version of
  // this bug, same class — so both styles get the same clamp and now agree.
  return clampToObserved(rollingMean(rows, key, TREND_ROLLING_WINDOW), rows, key);
}

export interface FitResult {
  /** Clone of `rows` with a `${key}${FIT_SUFFIX}` column per key that HAS a fit. */
  rows: WideRow[];
  /** The subset of `keys` that produced a line — the rest must not be rendered. */
  fittedKeys: string[];
}

/** Clone rows, adding a `${key}__fit` column for each key that yields a trend. */
export function withFit(rows: WideRow[], keys: string[], style: TrendStyle): FitResult {
  const cols: Record<string, (number | null)[]> = {};
  const fittedKeys: string[] = [];
  keys.forEach((k) => {
    const series = fitLine(rows, k, style);
    if (series) {
      cols[k] = series;
      fittedKeys.push(k);
    }
  });
  return {
    rows: rows.map((row, i) => {
      const r: WideRow = { ...row };
      fittedKeys.forEach((k) => {
        r[k + FIT_SUFFIX] = cols[k][i];
      });
      return r;
    }),
    fittedKeys,
  };
}

// ── trend preferences + localStorage persistence (Decision 10) ────────────────

/** Which series carry a trend, per block. Keys are block-scoped series ids. */
export interface TrendSeriesPrefs {
  ring: boolean;
  ipt: boolean;
  upsell: boolean;
  /**
   * Chart 2 — `orders_same_date`, the total-volume halo series. A DIFFERENT
   * order population from `storeTransactions` below (different cancel filter and
   * different day-boundary semantics), so neither is derivable from the other.
   */
  transactions: boolean;
  close: boolean;
  attempts: boolean;
  coverage: boolean;
  /** feat-179 Revenue & Transactions block — the money series (primary). */
  revenue: boolean;
  /**
   * feat-179 Revenue & Transactions block — the Ring-denominator count
   * (non-canceled orders only). DELIBERATELY separate from `transactions` above:
   * that one is chart 2's halo series, this one is this block's companion metric,
   * and they are different populations. Sharing a key would also make one card's
   * checkbox silently move the other card's line.
   */
  storeTransactions: boolean;
}

export interface TrendPrefs {
  /** Global TREND on/off (sticky bar). */
  on: boolean;
  /** Global trend style (sticky bar). */
  style: TrendStyle;
  /** Per-block series selection (Decision 4). */
  series: TrendSeriesPrefs;
  /**
   * Per-lens gate for the Ring & IPT block (Decision 6) — the only block with a
   * LENS switch. Default ON in Absolute, OFF in % of benchmark: Preston's own
   * uncertainty about coexisting with the 100% reference line, encoded as a
   * default rather than a ban.
   */
  lens: { absolute: boolean; indexed: boolean };
  /**
   * Per-block trend gate for blocks that have no LENS switch. feat-179's Revenue &
   * Transactions card is absolute-only by locked spec Decision 1 (no lens toggle,
   * not even behind a flag), so it gets its own gate rather than borrowing
   * `lens.absolute` — which belongs to the Ring & IPT card and would otherwise
   * couple the two cards' controls together.
   */
  blocks: { revenueTransactions: boolean };
}

/** Decision 9: primary series trend ON, companion series OFF — one line per panel. */
export const DEFAULT_TREND_PREFS: TrendPrefs = {
  on: true,
  style: 'linear',
  series: {
    ring: true,
    ipt: false,
    upsell: true,
    transactions: true,
    close: true,
    attempts: true,
    coverage: false,
    // feat-179 mirrors Decision 9: Revenue is this card's primary (ON),
    // Transactions is the opt-in companion (OFF) — one line per panel by default.
    revenue: true,
    storeTransactions: false,
  },
  lens: { absolute: true, indexed: false },
  blocks: { revenueTransactions: true },
};

export const TREND_PREFS_KEY = 'pilotTrends.trendPrefs.v1';

const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;

/**
 * Read the stored prefs, falling back to `DEFAULT_TREND_PREFS` field-by-field.
 *
 * MUST NEVER THROW: absent, unparseable, wrong-typed, or partially-corrupt values
 * all degrade to the defaults. This is an admin page — a bad localStorage value
 * must not white-screen it.
 */
export function loadTrendPrefs(storage?: Storage): TrendPrefs {
  const d = DEFAULT_TREND_PREFS;
  try {
    const store = storage ?? window.localStorage;
    const raw = store.getItem(TREND_PREFS_KEY);
    if (!raw) return d;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return d;
    const s = p.series && typeof p.series === 'object' ? p.series : {};
    const l = p.lens && typeof p.lens === 'object' ? p.lens : {};
    const b = p.blocks && typeof p.blocks === 'object' ? p.blocks : {};
    return {
      on: bool(p.on, d.on),
      style: p.style === 'linear' || p.style === 'rolling14' ? p.style : d.style,
      series: {
        ring: bool(s.ring, d.series.ring),
        ipt: bool(s.ipt, d.series.ipt),
        upsell: bool(s.upsell, d.series.upsell),
        transactions: bool(s.transactions, d.series.transactions),
        close: bool(s.close, d.series.close),
        attempts: bool(s.attempts, d.series.attempts),
        coverage: bool(s.coverage, d.series.coverage),
        revenue: bool(s.revenue, d.series.revenue),
        storeTransactions: bool(s.storeTransactions, d.series.storeTransactions),
      },
      lens: {
        absolute: bool(l.absolute, d.lens.absolute),
        indexed: bool(l.indexed, d.lens.indexed),
      },
      blocks: {
        // A v1 pref blob written before feat-179 has no `blocks` at all — it
        // falls through to the default here rather than reading as `false` and
        // silently shipping the new card with its trends off.
        revenueTransactions: bool(b.revenueTransactions, d.blocks.revenueTransactions),
      },
    };
  } catch {
    return d;
  }
}

/** Persist prefs; a full/blocked/absent storage is silently ignored (fail-soft). */
export function saveTrendPrefs(prefs: TrendPrefs, storage?: Storage): void {
  try {
    const store = storage ?? window.localStorage;
    store.setItem(TREND_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* quota exceeded / private mode / no storage — preferences just don't stick */
  }
}

export interface SeriesKey {
  key: string;
  name: string;
  color: string;
}

/**
 * Which wide-row keys to draw for a grouping mode + metric suffix, restricted to
 * `groups`. Charts 1/3/4 pass `['test']` because the control stores are the no-app
 * baseline and do NOT record (project_ll_control_stores_no_recording) — their
 * shift-anchored metrics are null by design, absent not zero.
 */
export function seriesKeys(
  mode: SeriesMode,
  suffix: string,
  stores: StoreWithColor[],
  groups: CohortGroup[] = ['test', 'control'],
): SeriesKey[] {
  if (mode === 'group') {
    const out: SeriesKey[] = [];
    if (groups.includes('test'))
      out.push({ key: 'testAvg' + suffix, name: 'Test group avg', color: PILOT_COLORS.test.a });
    if (groups.includes('control'))
      out.push({ key: 'ctrlAvg' + suffix, name: 'Control group avg', color: PILOT_COLORS.control.a });
    return out;
  }
  return stores
    .filter((s) => groups.includes(s.group))
    .map((s) => ({ key: s.location_id + suffix, name: s.name, color: s.color }));
}

/** Distinct close-rate criterion keys present anywhere in the series (for the drill-down). */
export function availableCriteria(resp: PilotTrendsResponse): string[] {
  const set = new Set<string>();
  for (const row of resp.series) {
    if (row.close_rate_by_criterion) {
      Object.keys(row.close_rate_by_criterion).forEach((k) => set.add(k));
    }
  }
  return Array.from(set).sort();
}

// ═════════════════════════════════════════════════════════════════════════════
// feat-229 — Mon–Fri WEEKLY bucketing for the long-view Ring/IPT card
//
// The backend serves DAILY rows over the extended window and never learns about
// weeks (spec "Contract change"); the rollup lives here, next to the other
// aggregations. Like `rollingMean` / `withIndexed` this is an AGGREGATION of
// backend-provided values, not metric math — the per-day `ring` / `ipt` /
// `revenue` / `transactions` formulas stay in the backend
// (memory: feedback_metrics_single_source).
//
// THE ONE THING TO KNOW BEFORE READING: the rendered array has **31 slots but 30
// buckets**. One null SPACER row keyed `2026-07-02` occupies the Jul 2–5 gap.
// It exists because on a categorical weekly axis the gap `ReferenceArea` has no
// category to anchor to — without it Recharts renders a zero-width band and a
// join that LOOKS continuous, silently and with no error. The spacer is a
// rendering slot, not a bucket: it carries no data, feeds no aggregate, and feeds
// no fit. Count buckets with `!row.spacer`. (spec AC6, DESIGN-NOTES §4.1)
// ═════════════════════════════════════════════════════════════════════════════

/** ISO date `n` days from `iso` (noon anchor dodges DST edges, like `eachDate`). */
function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Mon–Fri only. The four L&L stores are closed at weekends (spec domain facts). */
export function isOpenWeekday(iso: string): boolean {
  const dow = new Date(`${iso}T12:00:00`).getDay();
  return dow >= 1 && dow <= 5;
}

/** The Monday of `iso`'s ISO week. */
export function mondayOf(iso: string): string {
  const dow = new Date(`${iso}T12:00:00`).getDay();
  return addDays(iso, -(dow === 0 ? 6 : dow - 1));
}

/** `2026-01-20`,`2026-01-23` → `Jan 20 – Jan 23`. */
function rangeLabel(from: string, to: string): string {
  const f = (x: string) =>
    new Date(`${x}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${f(from)} – ${f(to)}`;
}

/** Long-view column names. Namespaced so NOTHING collides with the daily cards'
 *  keys (`t1_ring`, `t1_ring_idx`, `t1_ring__fit`, …) — the two cards render into
 *  the same DOM and share the page's test surface. */
export const LV = {
  ring: (id: string) => `${id}__lvRing`,
  ipt: (id: string) => `${id}__lvIpt`,
  /** `% of benchmark` twins. Named `Pct`, NOT `_idx`, for the same reason. */
  ringPct: (id: string) => `${id}__lvRingPct`,
  iptPct: (id: string) => `${id}__lvIptPct`,
  /** Open-day count for THIS store in THIS bucket (a store can be short alone). */
  open: (id: string) => `${id}__lvOpen`,
  /** The two trend fits (Ring only). Suffixes deliberately avoid `__fit`. */
  fitPre: (id: string) => `${id}__lvFitPre`,
  fitPilot: (id: string) => `${id}__lvFitPilot`,
} as const;

/** Per-phase segment column for a metric key — see `withPhaseSegments`. */
export const SEG = {
  pre: (key: string) => `${key}__segPre`,
  bench: (key: string) => `${key}__segBench`,
  pilot: (key: string) => `${key}__segPilot`,
} as const;

export interface WeeklyRow {
  /** x-axis category: the bucket's FIRST open weekday (its `dataKey`). */
  key: string;
  phase: PilotPhaseName | 'gap';
  /** `Jan 20 – Jan 23` — the human range, for tooltips and ticks. */
  label: string;
  /** Days in the bucket that produced a series row, max across stores. */
  openDays: number;
  /** `true` only for the gap spacer. A spacer is NOT a bucket. */
  spacer: boolean;
  [k: string]: number | string | boolean | null;
}

const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

/**
 * Mean of a store's DAILY `ring`/`ipt` over the benchmark-phase days.
 *
 * This is the `% of benchmark` denominator, and it is deliberately the SAME
 * mean-of-daily-values the existing card computes in `benchmarkMean` — spec AC12
 * requires the two cards' 100% reference to be one number so the long view
 * reconciles with everything else on the page. It is therefore NOT the
 * order-weighted benchmark aggregate, even though the plotted buckets ARE
 * order-weighted. That asymmetry is the spec's, and it is intentional.
 */
export function benchmarkDailyMean(
  resp: PilotTrendsResponse,
  locationId: string,
  metric: 'ring' | 'ipt',
): number | null {
  const vals = resp.series
    .filter((r) => r.location_id === locationId && phaseOf(r.date, resp.phases) === 'benchmark')
    .map((r) => r[metric])
    .filter((v): v is number => typeof v === 'number');
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * Daily series → Mon–Fri weekly buckets, one row per rendered slot.
 *
 * Rules, all load-bearing:
 *  1. **A bucket never spans a phase boundary.** Weeks are enumerated INSIDE each
 *     phase and clamped to it, so the benchmark's Jun 29 – Jul 1 stub is its own
 *     3-open-day bucket rather than being glued to the first pilot week.
 *  2. **Mon–Fri only.** Weekend dates belong to no bucket at all, so they cannot
 *     inflate an open-day count even if a (fake or malformed) row exists for one.
 *  3. **Order-weighted, never mean-of-daily-means** (spec AC7):
 *     `ring = Σrevenue ÷ Σtransactions`, `ipt = Σ(ipt×transactions) ÷ Σtransactions`.
 *     A busy Friday counts more than a slow Tuesday. A SHORT bucket is weighted
 *     exactly the same way — it is neither scaled to a 5-day equivalent nor
 *     equal-weighted against full weeks (spec AC9).
 *  4. **Each `gap` phase emits one null spacer row** and no buckets (see the
 *     header comment).
 *  5. `openDays` counts days that PRODUCED A ROW — a closed or zero-order day
 *     simply isn't there, and is never read as a zero.
 */
export function weeklyBuckets(
  resp: PilotTrendsResponse,
  stores?: PilotStore[],
): WeeklyRow[] {
  const ids = (stores ?? resp.stores).map((s) => s.location_id);

  const byDate = new Map<string, Map<string, PilotTrendsResponse['series'][number]>>();
  for (const row of resp.series) {
    if (!byDate.has(row.date)) byDate.set(row.date, new Map());
    byDate.get(row.date)!.set(row.location_id, row);
  }

  const baseline = new Map<string, { ring: number | null; ipt: number | null }>();
  ids.forEach((id) =>
    baseline.set(id, {
      ring: benchmarkDailyMean(resp, id, 'ring'),
      ipt: benchmarkDailyMean(resp, id, 'ipt'),
    }),
  );

  const rows: WeeklyRow[] = [];

  for (const phase of resp.phases) {
    if (phase.phase === 'gap') {
      // ── the spacer: a RENDERING slot, not a bucket ──────────────────────────
      const spacer: WeeklyRow = {
        key: phase.start,
        phase: 'gap',
        label: rangeLabel(phase.start, phase.end),
        openDays: 0,
        spacer: true,
      };
      ids.forEach((id) => {
        spacer[LV.ring(id)] = null;
        spacer[LV.ipt(id)] = null;
        spacer[LV.ringPct(id)] = null;
        spacer[LV.iptPct(id)] = null;
        spacer[LV.open(id)] = 0;
      });
      rows.push(spacer);
      continue;
    }

    let cursor = phase.start;
    while (cursor <= phase.end) {
      const mon = mondayOf(cursor);
      const weekStart = mon < phase.start ? phase.start : mon; // clamp — rule 1
      const fri = addDays(mon, 4);
      const weekEnd = fri > phase.end ? phase.end : fri;
      const days = eachDate(weekStart, weekEnd).filter(isOpenWeekday); // rule 2
      cursor = addDays(mon, 7);
      if (!days.length) continue; // a phase edge that lands on a weekend only

      const row: WeeklyRow = {
        key: days[0],
        phase: phase.phase,
        label: rangeLabel(days[0], days[days.length - 1]),
        openDays: 0,
        spacer: false,
      };

      ids.forEach((id) => {
        let rev = 0;
        let txnForRev = 0;
        let items = 0;
        let txnForIpt = 0;
        let open = 0;
        for (const d of days) {
          const r = byDate.get(d)?.get(id);
          if (!r) continue; // rule 5 — no row, no day
          open += 1;
          if (typeof r.revenue === 'number' && typeof r.transactions === 'number') {
            rev += r.revenue;
            txnForRev += r.transactions;
          }
          if (typeof r.ipt === 'number' && typeof r.transactions === 'number') {
            items += r.ipt * r.transactions;
            txnForIpt += r.transactions;
          }
        }
        const ring = txnForRev > 0 ? round6(rev / txnForRev) : null; // rule 3
        const ipt = txnForIpt > 0 ? round6(items / txnForIpt) : null;
        const b = baseline.get(id)!;
        row[LV.ring(id)] = ring;
        row[LV.ipt(id)] = ipt;
        row[LV.ringPct(id)] =
          ring != null && b.ring ? Math.round((ring / b.ring) * 1000) / 10 : null;
        row[LV.iptPct(id)] =
          ipt != null && b.ipt ? Math.round((ipt / b.ipt) * 1000) / 10 : null;
        row[LV.open(id)] = open;
        if (open > row.openDays) row.openDays = open;
      });

      rows.push(row);
    }
  }

  return rows;
}

/** Buckets only — the spacer is excluded. This is what AC6's "30" counts. */
export function bucketCount(rows: WeeklyRow[]): number {
  return rows.filter((r) => !r.spacer).length;
}

/**
 * Split each metric key into three per-phase columns so the card can draw the
 * pre-period at recessive ink weight and benchmark+pilot at full strength
 * (DESIGN-NOTES §2.2a) without moving a single pixel horizontally.
 *
 * ⚠ THE OVERLAP RULE (spec AC6b, DESIGN-NOTES §4.2). A naive split puts a visual
 * break at EVERY phase boundary, but only one boundary is a real break:
 *   • **pre → benchmark abut** (Jun 21 → Jun 22). The bench segment therefore
 *     reaches back and includes the LAST PRE bucket as its first point, so the
 *     joining segment is drawn exactly once, at benchmark weight. Without this the
 *     card grows a phantom gap in mid-June that does not exist in the data.
 *   • **benchmark → pilot do NOT abut** — the Jul 2–5 gap sits between them and is
 *     a real break. The pilot segment starts at its own first bucket and reaches
 *     back over nothing.
 *
 * Stated generally: a segment reaches back exactly one slot iff the immediately
 * preceding slot is a real bucket (not a spacer, not absent). That single rule
 * gets both boundaries right and keeps working if a phase list ever changes shape.
 */
export function withPhaseSegments(rows: WeeklyRow[], keys: string[]): WeeklyRow[] {
  const firstOf = (phase: string): number => rows.findIndex((r) => !r.spacer && r.phase === phase);
  const lastOf = (phase: string): number => {
    let last = -1;
    rows.forEach((r, i) => {
      if (!r.spacer && r.phase === phase) last = i;
    });
    return last;
  };
  /**
   * …iff the immediately preceding slot is a real bucket.
   *
   * ⚠ MEASURED: this spacer check is belt-and-braces, not the mechanism. Removing
   * it changes nothing observable, because the spacer's metric value is null and
   * the writer below emits null for a spacer row regardless of span — verified by
   * mutation (dropping the `!rows[first - 1].spacer` guard left all 67 tests
   * green). **The null spacer is what enforces the Jul 2–5 break.** The guard
   * still earns its place by keeping the SPAN honest, so a future reader of
   * `spans` isn't told the pilot segment starts one slot earlier than it draws.
   */
  const reachBack = (first: number): number =>
    first > 0 && !rows[first - 1].spacer ? first - 1 : first;

  const span = (phase: string, overlap: boolean): [number, number] | null => {
    const first = firstOf(phase);
    if (first === -1) return null;
    return [overlap ? reachBack(first) : first, lastOf(phase)];
  };

  const spans = {
    pre: span('pre', false), // nothing precedes the pre-period
    bench: span('benchmark', true), // abuts the pre-period → overlap by one
    pilot: span('pilot', true), // the spacer makes reachBack a no-op → real break
  };

  const inSpan = (s: [number, number] | null, i: number): boolean =>
    !!s && i >= s[0] && i <= s[1];

  return rows.map((row, i) => {
    const out: WeeklyRow = { ...row };
    keys.forEach((key) => {
      const v = row[key];
      const val = !row.spacer && typeof v === 'number' ? v : null;
      out[SEG.pre(key)] = inSpan(spans.pre, i) ? val : null;
      out[SEG.bench(key)] = inSpan(spans.bench, i) ? val : null;
      out[SEG.pilot(key)] = inSpan(spans.pilot, i) ? val : null;
    });
    return out;
  });
}

/** A phase band's category anchors on the weekly axis. */
export interface WeeklyBand {
  phase: PilotPhaseName | 'gap';
  x1: string;
  x2: string;
}

/**
 * Phase bands for the weekly axis, expressed as CONSECUTIVE BOUNDARIES.
 *
 * Recharts places categories at points, so a `ReferenceArea` spans category
 * CENTRE to category CENTRE. A gap band drawn `x1=x2=<the spacer>` is therefore
 * zero-width — the exact silent failure the spacer exists to prevent, reintroduced
 * one layer up. So each band runs from its own first slot to the NEXT region's
 * first slot (the last band ends at the last slot). With 22 pre + 2 benchmark +
 * 1 spacer + 6 pilot that yields widths of 22 / 2 / 1 / 5 slots: contiguous, no
 * unpainted slivers between phases, and every band non-zero.
 */
export function weeklyPhaseBands(rows: WeeklyRow[]): WeeklyBand[] {
  if (!rows.length) return [];
  const runs: { phase: PilotPhaseName | 'gap'; at: number }[] = [];
  rows.forEach((r, i) => {
    if (!runs.length || runs[runs.length - 1].phase !== r.phase) runs.push({ phase: r.phase, at: i });
  });
  return runs.map((run, k) => ({
    phase: run.phase,
    x1: rows[run.at].key,
    x2: rows[k + 1 < runs.length ? runs[k + 1].at : rows.length - 1].key,
  }));
}

/** A y-domain shared by all four panels, or the per-panel-auto fallback. */
export interface SharedDomain {
  /** Pass straight to `<YAxis domain={...}>`. */
  domain: [number, number] | ['auto', 'auto'];
  /** True when one store is such an outlier that a union would squash everyone. */
  fallback: boolean;
}

const AUTO: ['auto', 'auto'] = ['auto', 'auto'];

/**
 * ONE y-domain across the four panels — a REQUIRED deviation from the existing
 * card's `domain={['auto','auto']}` (`PilotTrends.tsx:866-867`), not an oversight.
 *
 * Per-panel auto fits each panel to its OWN store's range. A control store that
 * drifted $0.30 across seven months gets that $0.30 stretched to fill the panel and
 * renders as a rise visually identical to a test store's real $1.55 lift — a chart
 * that disproves the feature's own thesis by accident. (The design lead's first
 * render did exactly this.) DESIGN-NOTES §2.3.
 *
 * Union of every key's range ± 8% of that range.
 *
 * **Fallback — 2.2×, LEAVE-ONE-OUT (spec AC11d).** DESIGN-NOTES §2.3 originally
 * stated the trigger as "the union range is more than 2.2× the widest
 * single-store range". That divides two non-comparable quantities: the union
 * range carries the LEVEL SPREAD between stores, the single-store range carries
 * only movement. Four stores sitting at $20.8 … $23.9 therefore put the ratio
 * over the threshold on their own, and AC11b's stipulated fixture — one control
 * moving under $0.40, one test store over $1.50 — pushes it higher still. The
 * rule written to protect the shared-domain fix would have disabled it. Design
 * re-checked its own worked example, computed ~2.7× against its own 2.2×
 * threshold, and ratified the formulation below (2026-08-09).
 *
 * What ships: keep the 2.2× constant, measure what the note says it measures —
 * drop the single most extreme key and see whether the union collapses.
 * `unionRange(all) > 2.2 × min_k unionRange(all \ k)` fires when one series
 * really is far away (a fifth store on a different order of magnitude, the case
 * the note gives) and stays quiet when comparable stores merely sit at different
 * price levels.
 *
 * ⚠ **Guard: fewer than 3 keys with data ⇒ NEVER fall back.** Leave-one-out
 * degenerates at n = 2: removing one store leaves a single store's own range,
 * which is always far below a two-store union at different levels, so the rule
 * would fire unconditionally on every two-panel card. Below 3, the shared domain
 * is always the right answer. Callers pass ONE key per panel in the branch where
 * this matters, so "keys with data" is the panel count there.
 *
 * ⚠ **When this returns `fallback: true` the caller MUST swap the caption** to
 * say the panels are individually scaled. A card that has silently dropped to
 * per-panel scaling is the original defect wearing a different hat — the reader
 * cannot tell a flat control from a lifting test store and has not been told to
 * stop trying. That is a required behaviour, not a nicety (spec AC11d).
 *
 * The no-zero-floor rule from the existing card is preserved: a 0 floor compresses
 * the $20–26 band and kills the divergence read.
 */
function extentOf(rows: WeeklyRow[], keys: string[]): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  keys.forEach((key) => {
    rows.forEach((r) => {
      const v = r[key];
      if (typeof v !== 'number' || !Number.isFinite(v)) return;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    });
  });
  return lo === Infinity ? null : [lo, hi];
}

export function sharedDomain(rows: WeeklyRow[], keys: string[]): SharedDomain {
  const all = extentOf(rows, keys);
  if (!all) return { domain: AUTO, fallback: false };
  const [lo, hi] = all;
  const range = hi - lo;

  // Leave-one-out outlier check (see the doc comment). The `>= 3` is the AC11d
  // guard, NOT an off-by-one: at n = 2, "the union without one store" is just the
  // other store's own range, so the ratio is dominated by the level difference
  // between them and the rule fires on every two-panel card. Below 3, the shared
  // domain always wins.
  const MIN_PANELS_FOR_FALLBACK = 3;
  const withData = keys.filter((k) => extentOf(rows, [k]) !== null);
  if (withData.length >= MIN_PANELS_FOR_FALLBACK && range > 0) {
    let tightest = Infinity;
    withData.forEach((drop) => {
      const e = extentOf(rows, withData.filter((k) => k !== drop));
      if (e) tightest = Math.min(tightest, e[1] - e[0]);
    });
    if (tightest > 0 && range > 2.2 * tightest) return { domain: AUTO, fallback: true };
  }

  // A single flat series has range 0; pad off the level instead so the axis is
  // not degenerate. (`|| 1` covers the all-zeros case.)
  const pad = range * 0.08 || Math.abs(lo) * 0.05 || 1;
  return { domain: [lo - pad, hi + pad], fallback: false };
}

/**
 * The indexed lens' shared domain: `[82, 118]`, WIDENED (never narrowed) to hold
 * outliers. Everything orbits 100 here, so a shared domain is natural — the point
 * is only that all four panels get the SAME one.
 */
export function sharedIndexedDomain(rows: WeeklyRow[], keys: string[]): SharedDomain {
  const s = sharedDomain(rows, keys);
  if (s.domain[0] === 'auto') return { domain: [82, 118], fallback: false };
  const [lo, hi] = s.domain as [number, number];
  return { domain: [Math.min(82, lo), Math.max(118, hi)], fallback: false };
}

/** One fitted trend: its column, its point count, and whether it is provisional. */
export interface WeeklyFit {
  column: string;
  /** Buckets the fit was computed over. < 3 ⇒ render it visibly provisional. */
  points: number;
}

/** Per-store: the pre fit and the PILOT fit. Exactly two. Benchmark gets none. */
export type WeeklyFits = Record<
  string,
  { pre: WeeklyFit | null; pilotFit: WeeklyFit | null }
>;

function fitOver(
  rows: WeeklyRow[],
  key: string,
  include: (row: WeeklyRow, i: number) => boolean,
): { line: (number | null)[]; points: number } | null {
  const xs: number[] = [];
  const ys: number[] = [];
  rows.forEach((r, i) => {
    if (r.spacer || !include(r, i)) return; // the spacer feeds NO fit
    const v = r[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      xs.push(i);
      ys.push(v);
    }
  });
  if (xs.length < 2) return null; // never a flat line through one dot

  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx;
    sxx += dx * dx;
    sxy += dx * (ys[i] - my);
  }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  const intercept = my - slope * mx;

  const first = xs[0];
  const last = xs[n - 1];
  // Emit across the fit's own span only. The spacer stays NULL and the <Line>
  // carries `connectNulls`, so a fit whose span straddles the spacer still draws
  // through it. As of 2026-08-09 NEITHER fit straddles Jul 2–5 — pre ends Jun 21
  // and pilot starts Jul 6 — so nothing is drawn across the gap by either fit.
  // The connectNulls behaviour is retained deliberately: it is what keeps a fit
  // continuous across any FUTURE data-free slot inside its own span. Writing a
  // number onto the spacer row would put data on a row that must carry none.
  const line = rows.map((r, i) =>
    i < first || i > last || r.spacer ? null : round6(slope * i + intercept),
  );
  return { line, points: n };
}

/**
 * TWO fits per store, not three (spec AC11, Preston 2026-08-08, superseding the
 * original per-phase design):
 *
 *   1. **pre** — over the 22 pre-period buckets.
 *   2. **pilot** — over the 6 pilot buckets (W28–W33), starting the week of Jul 6.
 *
 * **Benchmark contributes to NO fit** (Preston 2026-08-09, superseding the
 * combined benchmark+pilot fit). Its 2 buckets were too short a period to carry a
 * trend of their own, and folding them into the pilot fit meant a level shift at
 * Jul 6 was partly reported as slope. With the phases fitted separately the visual
 * claim — "the slope changed at the pilot" — is read off two independent models.
 *
 * Consequence: neither fit spans the Jul 2–5 gap any more. Pre ends at Jun 21 and
 * pilot starts at Jul 6, so the gap now has no line of any kind across it.
 *
 * Ring only (DESIGN-NOTES §5): 2 fits × 2 metrics × 4 panels is a hairball, Ring is
 * the claim and IPT is the mechanism, and there is no stroke style left — solid is
 * raw Ring, dashed is raw IPT.
 */
export function withWeeklyFits(
  rows: WeeklyRow[],
  ids: string[],
  valueKey: (id: string) => string,
): { rows: WeeklyRow[]; fits: WeeklyFits } {
  const cols: Record<string, (number | null)[]> = {};
  const fits: WeeklyFits = {};

  ids.forEach((id) => {
    const key = valueKey(id);
    const pre = fitOver(rows, key, (r) => r.phase === 'pre');
    // PILOT ONLY — benchmark contributes to NO fit (Preston 2026-08-09). Its two
    // buckets were too short a period to carry a trend, and including them let a
    // level shift at Jul 6 be reported as slope.
    const bp = fitOver(rows, key, (r) => r.phase === 'pilot');
    fits[id] = {
      pre: pre ? { column: LV.fitPre(id), points: pre.points } : null,
      pilotFit: bp ? { column: LV.fitPilot(id), points: bp.points } : null,
    };
    if (pre) cols[LV.fitPre(id)] = pre.line;
    if (bp) cols[LV.fitPilot(id)] = bp.line;
  });

  return {
    rows: rows.map((row, i) => {
      const out: WeeklyRow = { ...row };
      Object.keys(cols).forEach((c) => {
        out[c] = cols[c][i];
      });
      return out;
    }),
    fits,
  };
}
